import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Row, Col, Table, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaChartPie, FaTrophy, FaPercentage, FaHome } from 'react-icons/fa';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import transactionService from '../services/transactionService';
import { formatCurrency } from '../utils/formatters';
import { useAppContext } from '../contexts/AppContext';
import { getCategoryTypeLabel, isExpenseType } from '../constants/categoryTypes';

// Registrar componentes do Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const CategoryAnalysis = () => {
  const { categories } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState({
    categories: [],
    totalExpenses: 0,
    totalIncomes: 0,
    totalTransactions: 0
  });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    analysisType: 'expenses' // 'expenses' ou 'incomes'
  });

  // Definir datas padrão (último mês)
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters(prev => ({
      ...prev,
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    }));
  }, []);

  const processTransactions = useCallback((transactions) => {
    const categoryStats = {};
    let totalExpenses = 0;
    let totalIncomes = 0;

    // Inicializar estatísticas das categorias
    categories.forEach(category => {
      categoryStats[category.id] = {
        id: category.id,
        name: category.name,
        type: category.type,
        amount: 0,
        transactionCount: 0,
        isExpense: isExpenseType(category.type)
      };
    });

    // Processar transações
    transactions.forEach(transaction => {
      if (transaction.categoryId && categoryStats[transaction.categoryId]) {
        const amount = Math.abs(transaction.amount);
        categoryStats[transaction.categoryId].amount += amount;
        categoryStats[transaction.categoryId].transactionCount += 1;

        if (transaction.nature === 'EXPENSE') {
          totalExpenses += amount;
        } else {
          totalIncomes += amount;
        }
      }
    });

    // Converter para array e filtrar por tipo
    const filteredCategories = Object.values(categoryStats)
      .filter(cat => {
        const hasMovement = cat.amount > 0;
        if (!hasMovement) return false;
        
        if (filters.analysisType === 'expenses') {
          return cat.isExpense;
        } else {
          return !cat.isExpense;
        }
      })
      .map(cat => ({
        ...cat,
        percentage: filters.analysisType === 'expenses' 
          ? (cat.amount / totalExpenses) * 100 
          : (cat.amount / totalIncomes) * 100,
        averagePerTransaction: cat.transactionCount > 0 ? cat.amount / cat.transactionCount : 0
      }))
      .sort((a, b) => b.amount - a.amount); // Ordenar por valor decrescente

    return {
      categories: filteredCategories,
      totalExpenses,
      totalIncomes,
      totalTransactions: transactions.length
    };
  }, [categories, filters.analysisType]);

  const loadAnalysisData = useCallback(async () => {
    if (!filters.startDate || !filters.endDate) return;
    
    try {
      setLoading(true);
      
      const transactions = await transactionService.search({
        startDate: filters.startDate,
        endDate: filters.endDate
      });
      
      const processedData = processTransactions(transactions);
      setAnalysisData(processedData);
      
    } catch (error) {
      console.error('Erro ao carregar dados da análise:', error);
      toast.error('Erro ao carregar análise. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate, processTransactions]);

  useEffect(() => {
    if (categories.length > 0) {
      loadAnalysisData();
    }
  }, [loadAnalysisData, categories]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    loadAnalysisData();
  };

  // Dados para o gráfico de pizza
  const pieChartData = {
    labels: analysisData.categories.slice(0, 10).map(cat => cat.name), // Top 10
    datasets: [
      {
        data: analysisData.categories.slice(0, 10).map(cat => cat.amount),
        backgroundColor: [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
          '#FF9F40', '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'
        ],
        hoverBackgroundColor: [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
          '#FF9F40', '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'
        ]
      }
    ]
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = formatCurrency(context.raw);
            const percentage = ((context.raw / (filters.analysisType === 'expenses' ? analysisData.totalExpenses : analysisData.totalIncomes)) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const getRankingIcon = (index) => {
    if (index === 0) return <FaTrophy className="text-warning" />;
    if (index === 1) return <FaTrophy className="text-secondary" />;
    if (index === 2) return <FaTrophy className="text-danger" />;
    return <span className="text-muted">{index + 1}</span>;
  };

  const totalAmount = filters.analysisType === 'expenses' ? analysisData.totalExpenses : analysisData.totalIncomes;

  return (
    <Container>
      <div className="sheet-container">
        <div className="sheet-header">
          <h5 className="sheet-title">
            <FaChartPie className="me-2" />
            Análise por Categoria
          </h5>
          <div className="sheet-toolbar">
            <Button 
              as={Link}
              to="/reports"
              variant="outline-primary"
            >
              <FaHome className="me-1" /> Central de Relatórios
            </Button>
          </div>
        </div>
        
        <div className="sheet-content">
          {/* Filtros */}
          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Data Inicial</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="excel-form-control"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Data Final</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="excel-form-control"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Tipo de Análise</Form.Label>
                <Form.Select
                  name="analysisType"
                  value={filters.analysisType}
                  onChange={handleFilterChange}
                  className="excel-form-control"
                >
                  <option value="expenses">Despesas</option>
                  <option value="incomes">Receitas</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button 
                variant="primary"
                onClick={handleApplyFilters}
                disabled={loading}
                className="mb-3 w-100"
              >
                {loading ? 'Carregando...' : 'Atualizar'}
              </Button>
            </Col>
          </Row>

          {/* Resumo */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="text-center">
                <Card.Body>
                  <h6 className="text-muted">Total {filters.analysisType === 'expenses' ? 'Despesas' : 'Receitas'}</h6>
                  <h4 className={filters.analysisType === 'expenses' ? 'text-danger' : 'text-success'}>
                    {formatCurrency(totalAmount)}
                  </h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <Card.Body>
                  <h6 className="text-muted">Categorias Ativas</h6>
                  <h4 className="text-primary">{analysisData.categories.length}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <Card.Body>
                  <h6 className="text-muted">Total Transações</h6>
                  <h4 className="text-info">{analysisData.totalTransactions}</h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center my-4">
              <span className="spinner-border text-primary" role="status"></span>
              <p className="mt-2">Carregando análise...</p>
            </div>
          ) : analysisData.categories.length > 0 ? (
            <>
              {/* Ranking e Gráfico */}
              <Row className="mb-4">
                {/* Ranking Top 10 */}
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">
                        <FaTrophy className="me-2 text-warning" />
                        Ranking de {filters.analysisType === 'expenses' ? 'Gastos' : 'Receitas'}
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      {analysisData.categories.slice(0, 10).map((category, index) => (
                        <div key={category.id} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                          <div className="d-flex align-items-center">
                            <div className="me-3" style={{ width: '24px', textAlign: 'center' }}>
                              {getRankingIcon(index)}
                            </div>
                            <div>
                              <strong>{category.name}</strong>
                              <br />
                              <small className="text-muted">{category.transactionCount} transações</small>
                            </div>
                          </div>
                          <div className="text-end">
                            <div className={`fw-bold ${filters.analysisType === 'expenses' ? 'text-danger' : 'text-success'}`}>
                              {formatCurrency(category.amount)}
                            </div>
                            <small className="text-muted">
                              <FaPercentage className="me-1" />
                              {category.percentage.toFixed(1)}%
                            </small>
                          </div>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </Col>

                {/* Gráfico de Pizza */}
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">
                        <FaChartPie className="me-2 text-primary" />
                        Distribuição por Categoria
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Pie data={pieChartData} options={pieChartOptions} />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Tabela Detalhada */}
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Análise Detalhada por Categoria</h6>
                </Card.Header>
                <Card.Body>
                  <Table responsive hover className="excel-table">
                    <thead>
                      <tr>
                        <th>Posição</th>
                        <th>Categoria</th>
                        <th>Tipo</th>
                        <th className="text-end">Valor Total</th>
                        <th className="text-center">Participação</th>
                        <th className="text-center">Transações</th>
                        <th className="text-end">Valor Médio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisData.categories.map((category, index) => (
                        <tr key={category.id}>
                          <td className="text-center">
                            {getRankingIcon(index)}
                          </td>
                          <td>
                            <strong>{category.name}</strong>
                          </td>
                          <td>
                            <small className="text-muted">
                              {getCategoryTypeLabel(category.type)}
                            </small>
                          </td>
                          <td className={`text-end fw-bold ${filters.analysisType === 'expenses' ? 'text-danger' : 'text-success'}`}>
                            {formatCurrency(category.amount)}
                          </td>
                          <td className="text-center">
                            <span className="badge bg-primary">
                              {category.percentage.toFixed(1)}%
                            </span>
                          </td>
                          <td className="text-center">
                            {category.transactionCount}
                          </td>
                          <td className="text-end">
                            {formatCurrency(category.averagePerTransaction)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div className="text-center my-4">
              <p>Nenhuma categoria encontrada para o período selecionado</p>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default CategoryAnalysis; 